import { useEffect, useMemo } from "react"
import { useXHR } from "@sembark-travel/xhr"
import { TWhatsAppThread, WhatsAppThreadXHR } from "../store"

/**
 * Mark a thread read while the operator is actually looking at it.
 *
 * Each surface decides when that is, hence `enabled`: the inbox treats an open
 * conversation as read, while the trip page keeps unread messages behind a
 * prompt until the operator engages with them.
 *
 * Gated on tab visibility either way — a thread left open in a backgrounded tab
 * would otherwise mark itself read as messages landed, clearing the count in
 * every other tab before anyone saw it. The `visibilitychange` listener covers
 * the other half: a message that arrived while hidden is marked read when the
 * operator returns to that tab, not left stale beneath their eyes.
 */
export function useMarkThreadRead(
	// Undefined while the thread is still loading; nothing to mark until then.
	thread: TWhatsAppThread | undefined,
	onRefresh: () => Promise<unknown>,
	enabled = true
) {
	const xhr = useXHR()
	const actions = useMemo(() => WhatsAppThreadXHR(xhr), [xhr])
	const threadId = thread?.id
	const unreadCount = thread?.unread_count

	useEffect(() => {
		if (!enabled || !unreadCount || !threadId) {
			return
		}
		let cancelled = false
		let ran = false
		const markRead = () => {
			// `ran` keeps rapid tab switching from firing the call repeatedly
			// before the refetch clears the count.
			if (cancelled || ran || document.hidden) {
				return
			}
			ran = true
			actions
				.markRead(threadId)
				.then(() => {
					if (!cancelled) {
						return onRefresh()
					}
				})
				.catch(() => undefined)
		}
		markRead()
		document.addEventListener("visibilitychange", markRead)
		return () => {
			cancelled = true
			document.removeEventListener("visibilitychange", markRead)
		}
	}, [enabled, threadId, unreadCount, actions, onRefresh])
}
